import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Search, CheckCircle, AlertCircle, ExternalLink, RefreshCw, TrendingUp, Globe, Code, Eye, ArrowLeft, Zap } from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import { LoadingState } from '../../components/ui/LoadingStates';
import EmptyState from '../../components/unified/EmptyState';
import UnifiedDashboardSidebar from '../../components/shared/UnifiedDashboardSidebar';
import UnifiedHeader from '../../components/unified/UnifiedHeader';

import googleJobsValidationService from '../../services/googleJobsValidationService';
import { supabase } from '../../lib/supabase';

export default function SEOValidationDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [stats, setStats] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    loadCompanyAndValidation();
  }, [user]);

  const loadCompanyAndValidation = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      // Get user's company
      const { data: profile } = await supabase?.from('profiles')?.select('company_id')?.eq('id', user?.id)?.single();

      if (!profile?.company_id) {
        console.error('No company found for user');
        setLoading(false);
        return;
      }

      setCompanyId(profile?.company_id);

      // Load validation data
      await loadValidationData(profile?.company_id);
    } catch (error) {
      console.error('Error loading validation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadValidationData = async (companyId) => {
    try {
      // Get validation stats
      const statsData = await googleJobsValidationService?.getCompanyValidationStats(companyId);
      setStats(statsData);

      // Get detailed validation results
      const results = await googleJobsValidationService?.validateAllCompanyJobs(companyId);
      setValidationResults(results);
    } catch (error) {
      console.error('Error loading validation data:', error);
    }
  };

  const handleRefresh = async () => {
    if (!companyId) return;
    setValidating(true);
    await loadValidationData(companyId);
    setValidating(false);
  };

  const handleViewJob = (jobId) => {
    navigate(`/job-details/${jobId}`);
  };

  const handleEditJob = (jobId) => {
    navigate(`/enhanced-job-creation-wizard/${jobId}`);
  };

  const handleTestRichResults = (url) => {
    window.open(url, '_blank');
  };

  const handleViewSchema = (job) => {
    setSelectedJob(job);
  };

  const filteredResults = validationResults?.filter(result =>
    result?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>SEO Validation Dashboard | Recrutees</title>
        <meta name="description" content="Validate your job postings for Google Jobs compliance and search visibility" />
      </Helmet>
      <div className="flex min-h-screen bg-gray-50">
        <UnifiedDashboardSidebar />

        <div className="flex-1">
          <UnifiedHeader onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

          <main className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <Button
                variant="ghost"
                onClick={() => navigate('/company-dashboard-hub')}
                className="mb-4 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>

              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-text mb-2">
                    SEO Validation Dashboard
                  </h1>
                  <p className="text-muted">
                    Ensure your job postings meet Google Jobs requirements for maximum visibility
                  </p>
                </div>

                <Button
                  onClick={handleRefresh}
                  disabled={validating}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${validating ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Stats Overview */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted text-sm">Total Jobs</span>
                    <Globe className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-3xl font-bold text-text">{stats?.totalJobs}</div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted text-sm">Compliant Jobs</span>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-green-600">{stats?.compliantJobs}</div>
                  <div className="text-xs text-muted mt-1">
                    {Math.round((stats?.compliantJobs / stats?.totalJobs) * 100)}% compliance rate
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted text-sm">Average Score</span>
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className={`text-3xl font-bold ${getScoreColor(stats?.averageScore)}`}>
                    {stats?.averageScore}%
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted text-sm">Needs Attention</span>
                    <AlertCircle className="w-5 h-5 text-orange-500" />
                  </div>
                  <div className="text-3xl font-bold text-orange-600">{stats?.jobsNeedingAttention}</div>
                  <div className="text-xs text-muted mt-1">
                    {stats?.totalIssues} issues, {stats?.totalWarnings} warnings
                  </div>
                </Card>
              </div>
            )}

            {/* Search */}
            <Card className="p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
                <Input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10"
                />
              </div>
            </Card>

            {/* Validation Results */}
            {filteredResults?.length === 0 ? (
              <EmptyState
                icon={Search}
                title="No jobs found"
                description="Try adjusting your search or create a new job posting"
                actionLabel="Create Job"
                onAction={() => navigate('/enhanced-job-creation-wizard')}
              />
            ) : (
              <div className="space-y-4">
                {filteredResults?.map((result) => (
                  <motion.div
                    key={result?.jobId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text mb-2">
                            {result?.title}
                          </h3>
                          
                          {/* Score Badge */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`px-3 py-1 rounded-full ${getScoreBgColor(result?.validation?.score)}`}>
                              <span className={`text-sm font-semibold ${getScoreColor(result?.validation?.score)}`}>
                                Score: {result?.validation?.score}%
                              </span>
                            </div>
                            
                            {result?.validation?.isCompliant ? (
                              <div className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">Compliant</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-orange-600">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">Needs Attention</span>
                              </div>
                            )}
                          </div>

                          {/* Summary */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted">Required Fields:</span>
                              <span className="ml-2 font-medium text-text">
                                {result?.validation?.summary?.requiredFieldsComplete}/{result?.validation?.summary?.requiredFieldsTotal}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted">Recommended:</span>
                              <span className="ml-2 font-medium text-text">
                                {result?.validation?.summary?.recommendedFieldsComplete}/{result?.validation?.summary?.recommendedFieldsTotal}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted">Issues:</span>
                              <span className="ml-2 font-medium text-red-600">
                                {result?.validation?.summary?.totalIssues}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted">Warnings:</span>
                              <span className="ml-2 font-medium text-yellow-600">
                                {result?.validation?.summary?.totalWarnings}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSchema(result)}
                            className="flex items-center gap-2"
                          >
                            <Code className="w-4 h-4" />
                            Schema
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestRichResults(result?.richResultsUrl)}
                            className="flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Test
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewJob(result?.jobId)}
                            className="flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          {!result?.validation?.isCompliant && (
                            <Button
                              size="sm"
                              onClick={() => handleEditJob(result?.jobId)}
                              className="flex items-center gap-2"
                            >
                              <Zap className="w-4 h-4" />
                              Fix
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Issues */}
                      {result?.validation?.issues?.length > 0 && (
                        <div className="mt-4 p-4 bg-red-50 rounded-lg">
                          <h4 className="text-sm font-semibold text-red-900 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Issues ({result?.validation?.issues?.length})
                          </h4>
                          <ul className="space-y-2">
                            {result?.validation?.issues?.map((issue, idx) => (
                              <li key={idx} className="text-sm text-red-800">
                                <span className="font-medium">{issue?.field}:</span> {issue?.message}
                                <span className="block text-red-600 text-xs mt-1">→ {issue?.fix}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Warnings */}
                      {result?.validation?.warnings?.length > 0 && (
                        <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                          <h4 className="text-sm font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Warnings ({result?.validation?.warnings?.length})
                          </h4>
                          <ul className="space-y-2">
                            {result?.validation?.warnings?.slice(0, 3)?.map((warning, idx) => (
                              <li key={idx} className="text-sm text-yellow-800">
                                <span className="font-medium">{warning?.field}:</span> {warning?.message}
                                <span className="block text-yellow-600 text-xs mt-1">→ {warning?.fix}</span>
                              </li>
                            ))}
                          </ul>
                          {result?.validation?.warnings?.length > 3 && (
                            <p className="text-xs text-yellow-700 mt-2">
                              +{result?.validation?.warnings?.length - 3} more warnings
                            </p>
                          )}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Resources */}
            <Card className="p-6 mt-6 bg-gradient-to-r from-blue-50 to-purple-50">
              <h3 className="text-lg font-semibold text-text mb-4">Google Jobs Resources</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a
                  href="https://search.google.com/test/rich-results"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <ExternalLink className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-text">Rich Results Test</div>
                    <div className="text-xs text-muted">Test your job schema</div>
                  </div>
                </a>
                <a
                  href={googleJobsValidationService?.getSearchConsoleUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <ExternalLink className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-text">Search Console</div>
                    <div className="text-xs text-muted">Check indexing status</div>
                  </div>
                </a>
                <a
                  href="https://developers.google.com/search/docs/appearance/structured-data/job-posting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <ExternalLink className="w-5 h-5 text-purple-600" />
                  <div>
                    <div className="font-medium text-text">Documentation</div>
                    <div className="text-xs text-muted">Google Jobs guidelines</div>
                  </div>
                </a>
              </div>
            </Card>
          </main>
        </div>
      </div>
      {/* Schema Preview Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-text">Schema Preview</h3>
                <Button variant="ghost" onClick={() => setSelectedJob(null)}>
                  Close
                </Button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{JSON.stringify(googleJobsValidationService?.generateJobPostingSchema(selectedJob), null, 2)}</code>
              </pre>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}